// Broker settlement position helpers (SM-621 phase 09a — SM-796 / SM-798 /
// SM-799). broker-service computes every figure; these helpers only put
// them into words. A direction is always an enum from the wire — never a
// sign the console interprets — and every amount is shown as a magnitude
// beside its direction ("We owe the broker 1,200.00"), never as a bare
// signed number.
import { useQueryClient, type QueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState, useSyncExternalStore } from "react";

import { canonToWire } from "@/components/brokers/receivables";
import { brokerKeys } from "@/lib/broker-queries";
import type {
  PaymentRequestItem,
  SettlementNoteDirection,
  SettlementNoteStatus,
  SettlementPositionDirection,
  SettlementPositionFigures,
} from "@/lib/broker-types";

import type { Tone } from "./governance";

/** The operational window the page opens on: the last 7 days (plan 09a decision #4). */
export const SETTLEMENT_DEFAULT_DAYS = 7;

export const SETTLEMENT_WINDOWS = [
  { days: 1, label: "Last 24 hours" },
  { days: 3, label: "Last 3 days" },
  { days: 7, label: "Last 7 days" },
  { days: 14, label: "Last 14 days" },
  { days: 30, label: "Last 30 days" },
] as const;

/**
 * The period [from, to) for "the last N days", ending at the next whole
 * minute so two reads a few seconds apart ask for the same period (and
 * so the export reproduces what the screen shows).
 */
export function settlementWindow(days: number, now = Date.now()): { from_unix_ms: number; to_unix_ms: number } {
  const minute = 60_000;
  const to = Math.ceil(now / minute) * minute;
  return { from_unix_ms: to - days * 24 * 60 * minute, to_unix_ms: to };
}

// SM-911: a write that changes a broker's money — a payout batch action, a
// payment request, a settlement note — renews every settlement window open
// on the page, so nothing is read again at the time it was opened (an old
// `to` misses what was just written and, past 15 minutes, the users'
// split). Each window takes a new `to` when the epoch moves.
let settleEpoch = 0;
const settleListeners = new Set<() => void>();

/** Renews every open settlement window; returns the new epoch. */
export function renewSettleWindows(): number {
  settleEpoch += 1;
  for (const l of settleListeners) l();
  return settleEpoch;
}

function subscribeSettleEpoch(l: () => void): () => void {
  settleListeners.add(l);
  return () => {
    settleListeners.delete(l);
  };
}

/** The renew epoch: it moves on every renewSettleWindows. */
export function useSettleEpoch(): number {
  return useSyncExternalStore(subscribeSettleEpoch, () => settleEpoch, () => settleEpoch);
}

/** A window older than this is renewed when the tab is shown again (and, on Brokers to settle, before an expand, "Show more" or a page change). */
export const SETTLE_WINDOW_MAX_AGE_MS = 60_000;

/** A window older than this is renewed while the page stays visible (SM-958): a page left open is not left at its first read. */
export const SETTLE_WINDOW_AUTO_RENEW_MS = 5 * 60_000;

interface RenewingWindow {
  epoch: number;
  days: number;
  reads: string;
  at: number;
  win: { from_unix_ms: number; to_unix_ms: number };
}

function takeRenewingWindow(epoch: number, days: number, reads: string, at = Date.now()): RenewingWindow {
  return { epoch, days, reads, at, win: settlementWindow(days, at) };
}

/**
 * A settlementWindow(days) that takes a new `to` whenever renewSettleWindows
 * runs, whenever `days` or `reads` changes (`reads` names everything else
 * the window is read with: the node, currency, scope, filters, page), when
 * the tab is shown again with the window over a minute old (SM-942), and
 * while the page stays visible, every SETTLE_WINDOW_AUTO_RENEW_MS (SM-958).
 * Otherwise it keeps its `to`, so a re-render never reads a new period.
 *
 * The last two renew through refetchAfterSettleWrite, as a write does:
 * every window on the page moves and every broker read is read again —
 * the payment-request and note lists beside the window included, which
 * renewing the window alone left at their first read (SM-958).
 */
export function useRenewingSettleWindow(days: number, reads = ""): { from_unix_ms: number; to_unix_ms: number } {
  const qc = useQueryClient();
  const epoch = useSettleEpoch();
  const [state, setState] = useState(() => takeRenewingWindow(epoch, days, reads));
  let current = state;
  // taken during render, so the new read never goes out at the old window first
  if (state.epoch !== epoch || state.days !== days || state.reads !== reads) {
    current = takeRenewingWindow(epoch, days, reads);
    setState(current);
  }
  const seen = useRef(current);
  useEffect(() => {
    seen.current = current;
  });
  useEffect(() => {
    // Several windows on one page share one renewal: the first renews them
    // all by moving the epoch, so the others — not yet re-rendered at the
    // new epoch — leave it.
    const renewIfOlder = (maxAge: number) => {
      if (document.visibilityState !== "visible" || seen.current.epoch !== settleEpoch) return;
      if (Date.now() - seen.current.at <= maxAge) return;
      void refetchAfterSettleWrite(qc, renewSettleWindows);
    };
    const onShown = () => renewIfOlder(SETTLE_WINDOW_MAX_AGE_MS);
    const tick = setInterval(() => renewIfOlder(SETTLE_WINDOW_AUTO_RENEW_MS), SETTLE_WINDOW_MAX_AGE_MS);
    document.addEventListener("visibilitychange", onShown);
    return () => {
      document.removeEventListener("visibilitychange", onShown);
      clearInterval(tick);
    };
  }, [qc]);
  return current.win;
}

/** A settlement-position read (one node or the list) whose window ends before `to`. */
function readsSettlementBefore(key: readonly unknown[], to: number): boolean {
  if (!key.includes("settlement-positions") && !key.includes("settlement-position")) return false;
  return key.some((k) => {
    const end = !!k && typeof k === "object" ? (k as { to_unix_ms?: unknown }).to_unix_ms : undefined;
    return typeof end === "number" && end < to;
  });
}

/**
 * After a write that changes a broker's money: `renew` moves the settlement
 * windows on the page to a new `to`, then the broker reads are fetched
 * again — all but a settlement-position read at an older `to`, which its
 * renewed window now reads afresh under a new key (SM-916): fetching it
 * again would read the old `to` only to throw the answer away, and hold
 * the dialog open meanwhile. A read already at the new `to` (renewed
 * within the same minute, so the same key) is read again. With no
 * `renew`, every broker read is.
 */
export function refetchAfterSettleWrite(qc: QueryClient, renew?: () => unknown): Promise<void> {
  if (!renew) return qc.invalidateQueries({ queryKey: brokerKeys.all });
  // taken before the windows renew, so no renewed `to` is earlier
  const to = settlementWindow(SETTLEMENT_DEFAULT_DAYS).to_unix_ms;
  renew();
  return qc.invalidateQueries({ queryKey: brokerKeys.all, predicate: (query) => !readsSettlementBefore(query.queryKey, to) });
}

export function directionLabel(d: SettlementPositionDirection | undefined): string {
  switch (d) {
    case "WE_OWE_BROKER":
      return "We owe the broker";
    case "BROKER_OWES_US":
      return "The broker owes us";
    case "SETTLED":
      return "Settled";
    default:
      return "—";
  }
}

export function directionTone(d: SettlementPositionDirection | undefined): Tone {
  switch (d) {
    case "WE_OWE_BROKER":
      return "accent";
    case "BROKER_OWES_US":
      return "warning";
    default:
      return "muted";
  }
}

/** |canonical| as a 4dp wire string for the money components (the sign is the direction's job). */
export function magnitudeWire(canon: string | undefined): string | undefined {
  if (!canon || !/^-?\d+$/.test(canon.trim())) return undefined;
  const v = canon.trim();
  return canonToWire(v.startsWith("-") ? v.slice(1) : v);
}

/** A canonical figure is below zero. */
export function isNegative(canon: string | undefined): boolean {
  return !!canon && canon.trim().startsWith("-") && /[1-9]/.test(canon);
}

export function noteDirectionLabel(d: SettlementNoteDirection | undefined): string {
  switch (d) {
    case "INBOUND":
      return "Inbound — the broker paid us";
    case "OUTBOUND":
      return "Outbound — we paid the broker";
    default:
      return "—";
  }
}

export function noteStatusLabel(s: SettlementNoteStatus | undefined, superseded: boolean): string {
  if (superseded) return "Superseded";
  switch (s) {
    case "PENDING":
      return "Pending — receipt not yet confirmed";
    case "CONFIRMED":
      return "Confirmed";
    default:
      return "—";
  }
}

export function noteStatusTone(s: SettlementNoteStatus | undefined, superseded: boolean): Tone {
  if (superseded) return "muted";
  return s === "CONFIRMED" ? "success" : "warning";
}

/** Today in the exchange's operating calendar (Asia/Bangkok), YYYY-MM-DD. */
export function operatingDate(now = new Date()): string {
  return new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Bangkok", year: "numeric", month: "2-digit", day: "2-digit" }).format(now);
}

// ── SM-878: admin-decided settlement ─────────────────────────────────
//
// The net position is information only. A payment request asks for the
// EXTERNAL CREDIT OWED (external_credit_outstanding), never the net, and
// commission is paid only by payout batch — the two are never offset.
// These helpers read server fields only; requestDefault is the console's
// one subtraction, and it only proposes a pre-fill.

type OwedFigures = Pick<
  SettlementPositionFigures,
  "external_credit_outstanding" | "external_credit_direction" | "payment_requests_open"
>;

const CANON = /^-?\d+$/;

/** |canonical| as a canonical string, or undefined when it is not one. */
function magnitudeCanon(canon: string | undefined): bigint | undefined {
  if (!canon || !CANON.test(canon.trim())) return undefined;
  const v = BigInt(canon.trim());
  return v < 0n ? -v : v;
}

/** The broker owes us external credit (external_credit_direction is BROKER_OWES_US). */
export function brokerOwesUs(f: Pick<SettlementPositionFigures, "external_credit_direction">): boolean {
  return f.external_credit_direction === "BROKER_OWES_US";
}

/**
 * The amount Request payment pre-fills, canonical 8dp: the external credit
 * the broker owes minus what open requests already ask for, never below
 * zero; "0" when the broker owes us nothing. undefined when the server
 * sent no external credit figure — then there is no default at all.
 */
export function requestDefault(f: OwedFigures): string | undefined {
  const owed = magnitudeCanon(f.external_credit_outstanding);
  if (owed === undefined) return undefined;
  if (!brokerOwesUs(f)) return "0";
  const open = magnitudeCanon(f.payment_requests_open?.outstanding_amount) ?? 0n;
  const rest = owed - open;
  return (rest > 0n ? rest : 0n).toString();
}

/**
 * The amount Record payment sent pre-fills, canonical 8dp: the external
 * credit we owe the broker. undefined (an empty field) unless we owe it.
 */
export function sendDefault(f: OwedFigures): string | undefined {
  if (f.external_credit_direction !== "WE_OWE_BROKER") return undefined;
  return magnitudeCanon(f.external_credit_outstanding)?.toString();
}

/** A canonical figure is above zero. */
export function isPositive(canon: string | undefined): boolean {
  return !!canon && CANON.test(canon.trim()) && BigInt(canon.trim()) > 0n;
}

/**
 * A canonical 8dp amount as the decimal an amount input shows, cut to
 * `dp` places (truncated, never rounded up) with trailing zeros dropped:
 * "123456780000" → "1234.5678". The caller keeps the canonical value to
 * send when the operator leaves the field untouched.
 */
export function canonToInput(canon: string | undefined, dp = 4): string {
  if (!canon || !CANON.test(canon.trim())) return "";
  let v = BigInt(canon.trim());
  const neg = v < 0n;
  if (neg) v = -v;
  const digits = v.toString().padStart(9, "0");
  const whole = digits.slice(0, -8).replace(/^0+(?=\d)/, "");
  const frac = digits.slice(-8, -8 + dp).replace(/0+$/, "");
  return `${neg ? "-" : ""}${whole}${frac ? `.${frac}` : ""}`;
}

/**
 * The users' P&L split lines under the external credit balance (SM-885,
 * confirmed by the PO 2026-10-09). Each line can be switched off here
 * alone: every screen that renders them reads this object. They are part
 * of the balance, never added to anything.
 */
export const SETTLEMENT_SPLIT_LINES = {
  openPositions: true,
  settledResults: true,
} as const;

export interface SplitLine {
  key: "openPositions" | "settledResults";
  label: string;
  canon: string;
  direction?: SettlementPositionDirection;
}

/**
 * The split lines to render for f: only the enabled ones the server sent.
 * The open-position cost always counts toward "the broker owes us"
 * (SM-912): an older broker-service sends no direction for it, so one is
 * derived from the cost alone — BROKER_OWES_US above 0, SETTLED at 0.
 */
export function settlementSplitLines(
  f: Pick<
    SettlementPositionFigures,
    "users_open_position_cost" | "users_open_position_direction" | "users_settled_result" | "users_settled_result_direction"
  >,
  enabled: { openPositions: boolean; settledResults: boolean } = SETTLEMENT_SPLIT_LINES,
): SplitLine[] {
  const lines: SplitLine[] = [];
  if (enabled.openPositions && f.users_open_position_cost !== undefined) {
    lines.push({
      key: "openPositions",
      label: "Users' open positions",
      canon: f.users_open_position_cost,
      direction: f.users_open_position_direction ?? (isPositive(f.users_open_position_cost) ? "BROKER_OWES_US" : "SETTLED"),
    });
  }
  if (enabled.settledResults && f.users_settled_result !== undefined) {
    lines.push({
      key: "settledResults",
      label: "Users' settled results and fees",
      canon: f.users_settled_result,
      direction: f.users_settled_result_direction,
    });
  }
  return lines;
}

// ── payment requests (SM-878 S4 / SM-888) ────────────────────────────

// `label` keys so the page-copy extractor carries every word to the
// Thai catalogue (it reads UI property names, not bare map values).
const REQUEST_STATUSES: Record<string, { label: string; tone: Tone }> = {
  REQUESTED: { label: "Requested", tone: "warning" },
  PAID: { label: "Paid", tone: "success" },
  CANCELLED: { label: "Cancelled", tone: "muted" },
};

export function paymentRequestStatusLabel(status: string | undefined): string {
  return (status && REQUEST_STATUSES[status]?.label) || status || "—";
}

export function paymentRequestStatusTone(status: string | undefined): Tone {
  return (status && REQUEST_STATUSES[status]?.tone) || "muted";
}

/**
 * A REQUESTED request whose due date is before today in the exchange's
 * operating calendar (Asia/Bangkok). Due today is not overdue; a paid or
 * cancelled request never is.
 */
export function isOverdue(r: Pick<PaymentRequestItem, "status" | "due_date">, today = operatingDate()): boolean {
  return r.status === "REQUESTED" && !!r.due_date && r.due_date < today;
}

/**
 * The server's captions on a page of payment requests, as the broker
 * portal shows them (SM-912): the ones every request carries, shown once
 * above the list, and, per row, the rest. They are the server's English,
 * rendered as data.
 */
export function splitRequestCaptions(items: readonly Pick<PaymentRequestItem, "captions">[]): {
  list: string[];
  other: (captions?: string[]) => string[];
} {
  const all = items.map((i) => i.captions ?? []);
  const list = all.length === 0 ? [] : all[0].filter((c) => all.every((cs) => cs.includes(c)));
  return { list, other: (captions) => (captions ?? []).filter((c) => !list.includes(c)) };
}
